import { useState, useMemo, useCallback, useEffect } from 'react';
import { debounce } from '@material-ui/core';
import { searchMembersForFeed } from '../../../../../util/APIUtils';
import { deduplicateSearchMembersAndGroupLocationsHistory, getErrorMsg } from '../../services/Feed.service';
import store, { changeToastType, showToast } from '../../../../core/store';
import {
  validateAdminLearnerSelection,
  isValidSelectionForConversation,
} from './helpers';

// Global styles for autocomplete dropdown (matching AttorneyModal)
const getGlobalFooterStyles = () => `
  .MuiAutocomplete-paper .dropdown-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    display: flex;
    padding: 24px 20px 12px 20px;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    z-index: 2;
  }
  
  .MuiAutocomplete-paper .dropdown-footer .footer-left {
    color: var(--success-green-green-dark, #228053);
    leading-trim: both;
    text-edge: cap;
    font-family: Inter;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.24px;
    margin-right: auto;
  }
  
  .MuiAutocomplete-paper .dropdown-footer .footer-right {
    color: var(--primary-blue-primary-deep, #0F2C54);
    text-align: right;
    leading-trim: both;
    text-edge: cap;
    font-family: Inter;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.24px;
  }

  /* Scrollbar styling for dropdown paper */
  .MuiAutocomplete-paper::-webkit-scrollbar {
    width: 6px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3.5px;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb {
    background: var(--Greyscale---grey-60, #CDD4E3);
    border-radius: 3.5px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb:hover {
    background: #B8C5D6;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb:active {
    background: #A3B6C9;
  }

  /* Listbox width and layout */
  .MuiAutocomplete-listbox {
    width: 100% !important;
    align-self: stretch !important;
  }

  /* Scrollbar styling for listbox */
  .MuiAutocomplete-listbox::-webkit-scrollbar {
    width: 6px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3.5px;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-thumb {
    background: var(--Greyscale---grey-60, #CDD4E3);
    border-radius: 3.5px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-thumb:hover {
    background: #B8C5D6;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-thumb:active {
    background: #A3B6C9;
  }

  /* Option padding */
  .MuiAutocomplete-listbox .MuiAutocomplete-option {
    padding-top: 10px !important;
    padding-bottom: 6px !important;
  }
`;

const SEARCH_ITEM_DELAY = 500;

export const useAdminLearnerModalHook = ({
  onCreateConversation,
  initialConversationSettings = {},
  isCreateLoading = false,
  onClose,
}) => {
  // State for admin search
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [isAdminSearchLoading, setIsAdminSearchLoading] = useState(false);
  const [adminInputValue, setAdminInputValue] = useState('');

  // State for learner search
  const [learnerSearchResults, setLearnerSearchResults] = useState([]);
  const [isLearnerSearchLoading, setIsLearnerSearchLoading] = useState(false);

  // State for selected admin (single object or null)
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // State for selected learners (array)
  const [selectedLearners, setSelectedLearners] = useState([]);

  // Conversation settings state
  const [learnerCanSendMessages, setLearnerCanSendMessages] = useState(
    initialConversationSettings.learnerCanSendMessages ?? true
  );
  const [limitLearnerMessages, setLimitLearnerMessages] = useState(
    initialConversationSettings.limitLearnerMessages ?? true
  );
  const [numberOfMessages, setNumberOfMessages] = useState(
    initialConversationSettings.numberOfMessages ?? 3
  );
  const [shutDownConversation, setShutDownConversation] = useState(
    initialConversationSettings.shutDownConversation ?? false
  );

  // Validation state
  const [memberSelectionError, setMemberSelectionError] = useState('');

  // Debounced search function for facility admins
  const searchAdmins = useMemo(
    () =>
      debounce(async (value) => {
        setIsAdminSearchLoading(true);
        try {
          const memberFeeds = await searchMembersForFeed(value, 'PERSON', false, true);
          memberFeeds.data = deduplicateSearchMembersAndGroupLocationsHistory(memberFeeds);
          setIsAdminSearchLoading(false);
          if (memberFeeds.status == 200) {
            setAdminSearchResults(memberFeeds.data);
            return;
          }
          store.dispatch(changeToastType("error"));
          store.dispatch(showToast(getErrorMsg(memberFeeds.status)));
        } catch (error) {
          setIsAdminSearchLoading(false);
          store.dispatch(changeToastType("error"));
          store.dispatch(showToast("Something went wrong while searching facility admins"));
        }
      }, SEARCH_ITEM_DELAY),
    []
  );

  // Debounced search function for learners (requires selectedAdmin)
  const searchLearners = useMemo(
    () =>
      debounce(async (value) => {
        if (!selectedAdmin || !selectedAdmin.id) {
          return;
        }
        setIsLearnerSearchLoading(true);
        try {
          const memberFeeds = await searchMembersForFeed(value, 'PERSON', false, true, selectedAdmin.id);
          memberFeeds.data = deduplicateSearchMembersAndGroupLocationsHistory(memberFeeds);
          setIsLearnerSearchLoading(false);
          if (memberFeeds.status == 200) {
            setLearnerSearchResults(memberFeeds.data);
            return;
          }
          store.dispatch(changeToastType("error"));
          store.dispatch(showToast(getErrorMsg(memberFeeds.status)));
        } catch (error) {
          setIsLearnerSearchLoading(false);
          store.dispatch(changeToastType("error"));
          store.dispatch(showToast("Something went wrong while searching learners"));
        }
      }, SEARCH_ITEM_DELAY),
    [selectedAdmin]
  );

  // Filter and map admin options to IMemberOption structure
  const adminOptions = useMemo(() => {
    if (!adminSearchResults || adminSearchResults.length === 0) {
      return [];
    }
    
    return adminSearchResults
      .filter((option) => {
        return option.type === 'person' || option.recordType === 'PERSON' || (!option.type && option.username);
      })
      .map((option) => {
        return {
          ...option,
          type: 'person',
          role: option.roleName || option.role || '',
          name: option.label || option.name || '',
          location: option.location || '',
        };
      });
  }, [adminSearchResults]);

  // Filter and map learner options to IMemberOption structure
  const learnerOptions = useMemo(() => {
    if (!learnerSearchResults || learnerSearchResults.length === 0) {
      return [];
    }
    
    return learnerSearchResults
      .filter((option) => {
        return option.type === 'person' || option.recordType === 'PERSON' || (!option.type && option.username);
      })
      .map((option) => {
        return {
          ...option,
          type: 'person',
          role: option.roleName || option.role || '',
          name: option.label || option.name || '',
          location: option.location || '',
        };
      });
  }, [learnerSearchResults]);

  // Handle admin change (single selection)
  const handleAdminChange = useCallback((newValue) => {
    // newValue is an array from autocomplete, extract first item
    const admin = Array.isArray(newValue) ? (newValue.length > 0 ? newValue[0] : null) : (newValue || null);
    setSelectedAdmin(admin);
    // Clear input value when admin is selected
    if (admin) {
      setAdminInputValue('');
    }
    // Clear learners when admin changes
    setSelectedLearners([]);
    setLearnerSearchResults([]);
  }, []);

  // Handle learners change (multiple selection)
  const handleLearnersChange = useCallback((newValue) => {
    setSelectedLearners(Array.isArray(newValue) ? newValue : []);
    // Clear error when learners are selected
    if (newValue && newValue.length > 0 && memberSelectionError) {
      setMemberSelectionError('');
    }
  }, [memberSelectionError]);

  // Handle admin search change
  const handleAdminSearchChange = useCallback((searchValue) => {
    setAdminInputValue(searchValue);
    if (searchValue.trim() !== '') {
      searchAdmins(searchValue);
    } else {
      setAdminSearchResults([]);
    }
  }, [searchAdmins]);

  // Handle learner search change
  const handleLearnerSearchChange = useCallback((searchValue) => {
    if (searchValue.trim() !== '' && selectedAdmin) {
      searchLearners(searchValue);
    } else {
      setLearnerSearchResults([]);
    }
  }, [searchLearners, selectedAdmin]);

  // Handle create conversation
  const handleCreateConversation = useCallback(() => {
    // Validate selection - must have exactly one admin and at least one learner
    const validation = validateAdminLearnerSelection(selectedAdmin, selectedLearners);

    if (!validation.isValid) {
      setMemberSelectionError(validation.error);
      if (validation.toastMessage) {
        store.dispatch(changeToastType("error"));
        store.dispatch(showToast(validation.toastMessage));
      }
      return;
    }

    // Clear error if validation passes
    setMemberSelectionError('');

    if (onCreateConversation) {
      // Combine selectedAdmin and selectedLearners into selectedMembers array
      // (similar to how AttorneyModal structures the data)
      const selectedMembers = [];
      if (selectedAdmin) {
        selectedMembers.push(selectedAdmin);
      }
      if (selectedLearners && selectedLearners.length > 0) {
        selectedMembers.push(...selectedLearners);
      }

      const conversationData = {
        selectedMembers: selectedMembers,
        conversationSettings: {
          learnerCanSendMessages,
          limitLearnerMessages,
          numberOfMessages,
          shutDownConversation,
        },
        isAdminLearnerConversation: true, // Flag to indicate this is an admin-learner conversation
      };
      onCreateConversation(conversationData);
    }
  }, [
    selectedAdmin,
    selectedLearners,
    learnerCanSendMessages,
    limitLearnerMessages,
    numberOfMessages,
    shutDownConversation,
    onCreateConversation,
  ]);

  // Handle close button keyboard events
  const handleCloseKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClose) {
        onClose();
      }
    }
  }, [onClose]);

  // Helper functions for autocomplete
  const getOptionLabel = useCallback((option) => {
    if (option.username) {
      const name = option.label || option.name;
      const searchableText = [option.username, option.docId, name]
        .filter(Boolean)
        .join(' ');
      return searchableText;
    }
    return option.label || option.name || '';
  }, []);

  const filterOptions = useCallback((options) => {
    return options;
  }, []);

  const isOptionEqualToValue = useCallback((option, value) => {
    if (option.username && value.username) {
      if (
        option.username &&
        value.username &&
        option.username !== '' &&
        value.username !== ''
      ) {
        return option.username === value.username;
      }
    }
    if (option.id && value.id) {
      return option.id === value.id;
    }
    const optionName = option.label || option.name;
    const valueName = value.label || value.name;
    return optionName === valueName;
  }, []);

  const getButtonDisabledState = useCallback(() => {
    if (isCreateLoading) {
      return true;
    }
    return !isValidSelectionForConversation(selectedAdmin, selectedLearners);
  }, [selectedAdmin, selectedLearners, isCreateLoading]);

  // Combine selected admin and learners for SelectedMembersSection
  const allSelectedMembers = useMemo(() => {
    const members = [];
    // selectedAdmin is a single object (not an array) when multiple={false}
    if (selectedAdmin) {
      members.push(selectedAdmin);
    }
    if (selectedLearners && selectedLearners.length > 0) {
      members.push(...selectedLearners);
    }
    return members;
  }, [selectedAdmin, selectedLearners]);

  // Handle remove member (for SelectedMembersSection)
  const handleRemoveMember = useCallback((memberToRemove) => {
    // Check if it's the admin
    if (selectedAdmin && 
        (selectedAdmin.id === memberToRemove.id || 
         selectedAdmin.username === memberToRemove.username)) {
      // When admin is removed, also clear all learners
      handleAdminChange(null);
      // Explicitly clear learners as well (handleAdminChange already does this, but being explicit)
      handleLearnersChange([]);
      return;
    }
    
    // Otherwise it's a learner
    const updatedLearners = selectedLearners.filter((member) => {
      if (member.id && memberToRemove.id) {
        return member.id !== memberToRemove.id;
      }
      if (member.username && memberToRemove.username) {
        return member.username !== memberToRemove.username;
      }
      const memberName = member.label || member.name;
      const removeName = memberToRemove.label || memberToRemove.name;
      return memberName !== removeName;
    });
    handleLearnersChange(updatedLearners);
  }, [selectedAdmin, selectedLearners, handleAdminChange, handleLearnersChange]);

  // Combine all options for SelectedMembersSection
  const allMemberOptions = useMemo(() => {
    return [...adminOptions, ...learnerOptions];
  }, [adminOptions, learnerOptions]);

  // Inject global styles for footer
  useEffect(() => {
    const styleId = 'admin-learner-autocomplete-footer-styles';
    
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = getGlobalFooterStyles();
    document.head.appendChild(style);

    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  // Helper function to apply paper styles
  const applyPaperStyles = (dropdownPaper, optionCount) => {
    if (!dropdownPaper) return;

    dropdownPaper.style.display = 'flex';
    dropdownPaper.style.paddingTop = '20px';
    dropdownPaper.style.flexDirection = 'column';
    dropdownPaper.style.alignItems = 'flex-start';
    dropdownPaper.style.alignSelf = 'stretch';

    dropdownPaper.style.borderRadius = '24px';
    dropdownPaper.style.background = '#FFF';
    dropdownPaper.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)';
    dropdownPaper.style.position = 'relative';

    dropdownPaper.style.scrollbarWidth = 'thin';
    dropdownPaper.style.scrollbarColor = 'var(--Greyscale---grey-60, #CDD4E3) transparent';

    if (optionCount === 0) {
      dropdownPaper.style.minHeight = '100px';
      dropdownPaper.style.overflowY = 'hidden';
    } else if (optionCount === 1) {
      dropdownPaper.style.minHeight = '170px';
      dropdownPaper.style.overflowY = 'hidden';
    } else {
      dropdownPaper.style.overflowY = 'auto';
    }
  };

  // Helper function to apply listbox styles
  const applyListboxStyles = (listbox) => {
    if (!listbox) return;

    listbox.style.width = '100%';
    listbox.style.alignSelf = 'stretch';

    listbox.style.paddingTop = '0px';
    listbox.style.paddingBottom = '50px';
    listbox.style.paddingLeft = '0px';
    listbox.style.paddingRight = '0px';

    listbox.style.scrollbarWidth = 'thin';
    listbox.style.scrollbarColor = 'var(--Greyscale---grey-60, #CDD4E3) transparent';
  };

  // Helper function to create and style footer
  const applyFooterStyles = (dropdownPaper, memberOptions) => {
    if (!dropdownPaper) return;

    const existingFooter = dropdownPaper.querySelector('.dropdown-footer');
    if (existingFooter) {
      existingFooter.remove();
    }

    let gradientOverlay = dropdownPaper.querySelector('.dropdown-gradient-overlay');
    if (!gradientOverlay) {
      gradientOverlay = document.createElement('div');
      gradientOverlay.className = 'dropdown-gradient-overlay';
      gradientOverlay.style.cssText = `
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 50px;
        background: linear-gradient(to top, white 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
        pointer-events: none;
        z-index: 1;
      `;
      dropdownPaper.appendChild(gradientOverlay);
    }

    const footer = document.createElement('div');
    footer.className = 'dropdown-footer';
    footer.style.position = 'absolute';
    footer.style.bottom = '0';
    footer.style.left = '0';
    footer.style.right = '0';
    footer.style.background = 'white';
    footer.style.display = 'flex';
    footer.style.padding = '24px 20px 12px 20px';
    footer.style.justifyContent = 'flex-end';
    footer.style.alignItems = 'center';
    footer.style.gap = '4px';
    footer.style.alignSelf = 'stretch';
    footer.style.zIndex = '2';

    const footerLeft = document.createElement('div');
    footerLeft.className = 'footer-left';
    footerLeft.textContent = 'Be specific for quicker results';
    footerLeft.style.color = 'var(--success-green-green-dark, #228053)';
    footerLeft.style.fontFamily = 'Inter';
    footerLeft.style.fontSize = '12px';
    footerLeft.style.fontStyle = 'normal';
    footerLeft.style.fontWeight = '400';
    footerLeft.style.lineHeight = '22px';
    footerLeft.style.letterSpacing = '0.24px';
    footerLeft.style.marginRight = 'auto';

    const footerRight = document.createElement('div');
    footerRight.className = 'footer-right';
    const resultCount = memberOptions.length;
    footerRight.textContent = `${resultCount}/${resultCount} Results`;
    footerRight.style.color = 'var(--primary-blue-primary-deep, #0F2C54)';
    footerRight.style.textAlign = 'right';
    footerRight.style.fontFamily = 'Inter';
    footerRight.style.fontSize = '12px';
    footerRight.style.fontStyle = 'normal';
    footerRight.style.fontWeight = '400';
    footerRight.style.lineHeight = '22px';
    footerRight.style.letterSpacing = '0.24px';

    footer.appendChild(footerLeft);
    footer.appendChild(footerRight);
    dropdownPaper.appendChild(footer);
  };

  // Main orchestrator function to setup all dropdown styles
  const setupDropdownStyles = (dropdownPaper, listbox, memberOptions) => {
    if (!dropdownPaper) return;

    const optionCount = memberOptions.length;

    applyPaperStyles(dropdownPaper, optionCount);

    if (listbox) {
      applyListboxStyles(listbox);
    }

    applyFooterStyles(dropdownPaper, memberOptions);
  };

  // Footer injection for admin autocomplete
  useEffect(() => {
    const getAutocompleteRoot = () => {
      return document.querySelector('.admin-autocomplete-root');
    };

    const addFooterToDropdown = () => {
      const autocompleteRoot = getAutocompleteRoot();
      if (!autocompleteRoot) return;
      
      const dropdownPaper = autocompleteRoot.querySelector('.MuiAutocomplete-paper') || 
                            document.querySelector('.MuiAutocomplete-paper');
      
      const listbox = dropdownPaper?.querySelector('.MuiAutocomplete-listbox') || null;

      if (dropdownPaper) {
        setupDropdownStyles(dropdownPaper, listbox, adminOptions);
      }
    };

    const updateFooter = () => {
      const autocompleteRoot = getAutocompleteRoot();
      if (!autocompleteRoot) return;
      
      const dropdownPaper = autocompleteRoot.querySelector('.MuiAutocomplete-paper') || 
                            document.querySelector('.MuiAutocomplete-paper');
      
      if (dropdownPaper) {
        const listbox = dropdownPaper.querySelector('.MuiAutocomplete-listbox') || null;
        setupDropdownStyles(dropdownPaper, listbox, adminOptions);
      }
    };

    const timer = setTimeout(addFooterToDropdown, 100);

    const handleFocus = () => {
      setTimeout(addFooterToDropdown, 100);
    };

    const handleDropdownOpen = () => {
      setTimeout(() => {
        addFooterToDropdown();
        updateFooter();
      }, 100);
    };

    let autocompleteRoot = null;
    let inputElement = null;
    let observer = null;

    const setupEventListeners = () => {
      const root = getAutocompleteRoot();
      if (!root) return;
      
      autocompleteRoot = root;
      inputElement = autocompleteRoot.querySelector('.MuiAutocomplete-inputRoot input') ||
                     document.querySelector('.MuiAutocomplete-inputRoot input');
      
      if (inputElement) {
        inputElement.addEventListener('focus', handleFocus);
      }

      autocompleteRoot.addEventListener('click', handleDropdownOpen);

      observer = new MutationObserver(() => {
        setTimeout(addFooterToDropdown, 100);
      });

      observer.observe(autocompleteRoot, {
        childList: true,
        subtree: true,
      });
    };

    const setupTimer = setTimeout(setupEventListeners, 100);

    return () => {
      clearTimeout(timer);
      clearTimeout(setupTimer);
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
      }
      if (autocompleteRoot) {
        autocompleteRoot.removeEventListener('click', handleDropdownOpen);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [adminOptions]);

  // Footer injection for learner autocomplete
  useEffect(() => {
    const getAutocompleteRoot = () => {
      return document.querySelector('.learner-autocomplete-root');
    };

    const addFooterToDropdown = () => {
      const autocompleteRoot = getAutocompleteRoot();
      if (!autocompleteRoot) return;
      
      const dropdownPaper = autocompleteRoot.querySelector('.MuiAutocomplete-paper') || 
                            document.querySelector('.MuiAutocomplete-paper');
      
      const listbox = dropdownPaper?.querySelector('.MuiAutocomplete-listbox') || null;

      if (dropdownPaper) {
        setupDropdownStyles(dropdownPaper, listbox, learnerOptions);
      }
    };

    const updateFooter = () => {
      const autocompleteRoot = getAutocompleteRoot();
      if (!autocompleteRoot) return;
      
      const dropdownPaper = autocompleteRoot.querySelector('.MuiAutocomplete-paper') || 
                            document.querySelector('.MuiAutocomplete-paper');
      
      if (dropdownPaper) {
        const listbox = dropdownPaper.querySelector('.MuiAutocomplete-listbox') || null;
        setupDropdownStyles(dropdownPaper, listbox, learnerOptions);
      }
    };

    const timer = setTimeout(addFooterToDropdown, 100);

    const handleFocus = () => {
      setTimeout(addFooterToDropdown, 100);
    };

    const handleDropdownOpen = () => {
      setTimeout(() => {
        addFooterToDropdown();
        updateFooter();
      }, 100);
    };

    let autocompleteRoot = null;
    let inputElement = null;
    let observer = null;

    const setupEventListeners = () => {
      const root = getAutocompleteRoot();
      if (!root) return;
      
      autocompleteRoot = root;
      inputElement = autocompleteRoot.querySelector('.MuiAutocomplete-inputRoot input') ||
                     document.querySelector('.MuiAutocomplete-inputRoot input');
      
      if (inputElement) {
        inputElement.addEventListener('focus', handleFocus);
      }

      autocompleteRoot.addEventListener('click', handleDropdownOpen);

      observer = new MutationObserver(() => {
        setTimeout(addFooterToDropdown, 100);
      });

      observer.observe(autocompleteRoot, {
        childList: true,
        subtree: true,
      });
    };

    const setupTimer = setTimeout(setupEventListeners, 100);

    return () => {
      clearTimeout(timer);
      clearTimeout(setupTimer);
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
      }
      if (autocompleteRoot) {
        autocompleteRoot.removeEventListener('click', handleDropdownOpen);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [learnerOptions]);

  return {
    // Admin autocomplete props
    adminOptions,
    selectedAdmin: selectedAdmin, // Return as single object for single-select autocomplete
    selectedAdminObject: selectedAdmin, // Return as single object for validation
    adminInputValue, // Input value for controlled input
    isAdminSearchLoading,
    handleAdminChange,
    handleAdminSearchChange,

    // Learner autocomplete props
    learnerOptions,
    selectedLearners,
    isLearnerSearchLoading,
    handleLearnersChange,
    handleLearnerSearchChange,

    // Conversation settings
    learnerCanSendMessages,
    limitLearnerMessages,
    numberOfMessages,
    shutDownConversation,
    setLearnerCanSendMessages,
    setLimitLearnerMessages,
    setNumberOfMessages,
    setShutDownConversation,

    // Validation and actions
    memberSelectionError,
    handleCreateConversation,
    getOptionLabel,
    filterOptions,
    isOptionEqualToValue,
    getButtonDisabledState,

    // Close handler
    handleCloseKeyDown,

    // Selected members section props
    allSelectedMembers,
    handleRemoveMember,
    allMemberOptions,
  };
};
